import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const MAX_FREE_GENERATIONS = 3;

interface GenerationsState {
  generationsLeft: number;
  loading: boolean;
  error: string | null;
  resetAt: Date | null;
}

export const useGenerations = () => {
  const { user } = useAuth();
  const [state, setState] = useState<GenerationsState>({
    generationsLeft: MAX_FREE_GENERATIONS,
    loading: true,
    error: null,
    resetAt: null,
  });

  const fetchGenerations = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Fetch current generation record
      const { data, error } = await supabase
        .from("user_generations")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        // No record exists, user has full generations
        setState({
          generationsLeft: MAX_FREE_GENERATIONS,
          loading: false,
          error: null,
          resetAt: null,
        });
        return;
      }

      const resetAt = new Date(data.reset_at);
      const now = new Date();

      // Check if reset time has passed
      if (now >= resetAt) {
        // Reset the generations
        const newResetAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        await supabase
          .from("user_generations")
          .update({
            generations_used: 0,
            reset_at: newResetAt.toISOString(),
          })
          .eq("user_id", user.id);

        setState({
          generationsLeft: MAX_FREE_GENERATIONS,
          loading: false,
          error: null,
          resetAt: newResetAt,
        });
      } else {
        setState({
          generationsLeft: MAX_FREE_GENERATIONS - data.generations_used,
          loading: false,
          error: null,
          resetAt,
        });
      }
    } catch (err) {
      console.error("Error fetching generations:", err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: "Failed to fetch generation count",
      }));
    }
  }, [user]);

  const useGeneration = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    try {
      // Check current state
      const { data: existing } = await supabase
        .from("user_generations")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      const now = new Date();

      if (!existing) {
        // Create new record with 1 generation used
        const resetAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        await supabase.from("user_generations").insert({
          user_id: user.id,
          generations_used: 1,
          reset_at: resetAt.toISOString(),
        });

        setState({
          generationsLeft: MAX_FREE_GENERATIONS - 1,
          loading: false,
          error: null,
          resetAt,
        });
        return true;
      }

      const resetAt = new Date(existing.reset_at);

      // Check if reset time has passed
      if (now >= resetAt) {
        // Reset and use 1
        const newResetAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        await supabase
          .from("user_generations")
          .update({
            generations_used: 1,
            reset_at: newResetAt.toISOString(),
          })
          .eq("user_id", user.id);

        setState({
          generationsLeft: MAX_FREE_GENERATIONS - 1,
          loading: false,
          error: null,
          resetAt: newResetAt,
        });
        return true;
      }

      // Check if user has generations left
      if (existing.generations_used >= MAX_FREE_GENERATIONS) {
        return false;
      }

      // Use a generation
      const newUsed = existing.generations_used + 1;
      await supabase
        .from("user_generations")
        .update({ generations_used: newUsed })
        .eq("user_id", user.id);

      setState({
        generationsLeft: MAX_FREE_GENERATIONS - newUsed,
        loading: false,
        error: null,
        resetAt,
      });
      return true;
    } catch (err) {
      console.error("Error using generation:", err);
      return false;
    }
  }, [user]);

  useEffect(() => {
    fetchGenerations();
  }, [fetchGenerations]);

  return {
    ...state,
    maxGenerations: MAX_FREE_GENERATIONS,
    useGeneration,
    refreshGenerations: fetchGenerations,
  };
};
